/**
 * SyncRemoteConfigSettingsChangesUseCase
 *
 * Handler behind the `versionDefaultSettings` Remote Config key: re-read the
 * global `/settings` collection, rewrite the signed-in user's
 * `/users/{uid}/userSettings` subcollection from it, and only then refresh the
 * local encrypted `USER_SETTINGS` cache.
 *
 * Mirrors Flutter's `SyncRepository.syncRemoteConfigSettingsChanges()`
 * (lib/data/repository/sync_repository.dart:571-584), which the Remote Config
 * real-time listener invokes for that key
 * (lib/services/remote_config_service.dart:229-233). Flutter's three steps and
 * their order are preserved: `getDefaultSettings()` (:575-576) →
 * `syncUserSettings(userSettings)` (:577) → **only if it returned true** (:578)
 * → `_syncUserSettings()` (:579). The `if (saved)` gate is load-bearing: a failed
 * server rewrite must not be followed by a local re-sync, or the cache would be
 * refreshed from a subcollection that was never updated.
 *
 * Step 3 delegates to `SyncSecureStorageUseCase.resyncEntity(USER_SETTINGS)`
 * rather than duplicating Flutter's private `_syncUserSettings()`
 * (sync_repository.dart:543-569). That local-cache half — fetch the user's
 * settings, drop the cached entries the server no longer returns, then write the
 * rest — already exists in full on the web in
 * `syncSecureStorageUseCase.ts` (the `USER_SETTINGS` sync path).
 *
 * Reset-to-defaults on a version bump (deliberately mirrored): the rewrite clears
 * every existing `userSettings` document before the write batch
 * (lib/data/data_sources/user_settings_data_source.dart:129), so values the user
 * toggled are reset to their defaults. The Flutter comment at :139-141 claims the
 * `merge: true` "avoids overriding setting value set by user"; the clear at :129
 * makes that merge a no-op, so the code — not the comment — is the contract, and
 * the web reproduces the code.
 *
 * DELIBERATE DIVERGENCE — corrects a Flutter data-loss bug (one, on this path):
 * the empty-defaults early return below. Flutter calls
 * `syncUserSettings(userSettings)` unconditionally at sync_repository.dart:577,
 * including with an empty list; `syncUserSettings` runs `await
 * _clearUserSettings()` BEFORE its write batch
 * (user_settings_data_source.dart:129); and `_clearUserSettings` deletes EVERY
 * document under `users/{uid}/userSettings` (:156-180). With an empty list the
 * write batch is vacuous but the clear is not — Flutter wipes the subcollection,
 * commits an empty batch and still returns true (:153), so `saved == true` at
 * :578 and the local cache is then re-synced from the now-empty server state.
 * The empty list is reachable in both clients because `getDefaultSettings()`
 * swallows read errors and also returns `[]` for a signed-out caller
 * (user_settings_data_source.dart:36-39,48-50) — `SyncService.getDefaultSettings`
 * carries the same contract. Per `.claude/context/conventions.md` → "Handling
 * obvious Flutter bugs", the guard stays and is recorded here; the Flutter source
 * is NOT patched, and a follow-up Flutter fix is recommended. The same citation is
 * carried one layer down on `FirestoreService.replaceUserSettings`, which has its
 * own empty-list guard.
 */

import { SyncService } from '@/domain/services/sync/syncService';
import { SyncSecureStorageUseCase } from '@/domain/usecases/sync/syncSecureStorageUseCase';
import { SyncEntityType } from '@/domain/enums/syncEntityType';

export class SyncRemoteConfigSettingsChangesUseCase {
  private syncService: SyncService;
  private syncSecureStorage: SyncSecureStorageUseCase;

  constructor(syncService?: SyncService, syncSecureStorage?: SyncSecureStorageUseCase) {
    this.syncService = syncService || new SyncService();
    this.syncSecureStorage = syncSecureStorage || new SyncSecureStorageUseCase();
  }

  async execute(): Promise<void> {
    // 1. Read the global defaults (sync_repository.dart:575-576).
    const defaults = await this.syncService.getDefaultSettings();
    if (defaults.length === 0) {
      // Divergence documented in the file header: writing an empty set would
      // clear the user's settings on both clients.
      return;
    }

    // 2. Rewrite the user's subcollection from them (sync_repository.dart:577).
    const saved = await this.syncService.saveUserSettingsFromDefaults(defaults);
    if (!saved) {
      // Flutter's `if (saved)` gate (sync_repository.dart:578).
      return;
    }

    // 3. Refresh the local encrypted cache (sync_repository.dart:579 →
    //    `_syncUserSettings()`), delegated to the existing web sync path.
    await this.syncSecureStorage.resyncEntity(SyncEntityType.USER_SETTINGS);
  }
}

export const syncRemoteConfigSettingsChangesUseCase = new SyncRemoteConfigSettingsChangesUseCase();
